'use strict';

import _ from 'underscore';
import $ from 'jquery';
import utilities from '../../js/utilities';
import config from '../../js/config';
import {EnableHybrisIntegration} from '../../utilities/featureFlags';

// This object is auto-initialized and included in WWWEntryPoints.js
class AnalyticsCart {

  /**
   * Scrape category from search results using partId
   *
   * @param {string} partId
   * @returns {string}
   * @private
   */
  _getCategoryFromId(partId) {
    let $items = $('.SearchResults-column--name');
    let category = '';

    $items.each(function(i, item) {
      let $this = $(item);
      if ($this.data('partId') && $this.data('partId').toString() === partId) {
        category = $this.data('fullCategory');
        return false;
      }
    });

    return category;
  }

  /**
   * This is a temporary measure to get the current category from where it exists in the page
   *
   * This will be improved to not scrape the page and get the full supplied category from a more accessible
   * variable as part of ARW-3597
   *
   * @param {string} partId
   * @returns {string}
   * @private
   */
  _scrapeCategory(partId) {
    // Are we on the PDP page or Search page?
    let $category = $('.Part-Summary-Details-category');
    if ($category.length) {
      return $category.text().trim();
    }
    return this._getCategoryFromId(partId);
  }

  //Infers correct Part Number if Hybris intergration enabled
  getPartNumber(cartEntry) {
    let partNumber = cartEntry.catalogPartNo;
    if (EnableHybrisIntegration.isEnabled()) {
      partNumber = cartEntry.partNo;
    }
    return partNumber;
  }

  /**
   * Get the added item details for adobe datalayer eCommerce
   *
   * @param {object} data
   * @param {AddedProduct[]} data.addedProducts - The product just added
   * @param {CartEntry[]} data.entries - Cart entries
   * @returns {Array}
   */
  getAddedItemDetailAdobeFormat(data) {
    return _.map(data.addedProducts, addedProduct => {
      const buyingOption = addedProduct.model;
      const cartEntry = data.entries.find((cartItem)=> { return this.getPartNumber(cartItem) === buyingOption.partId.toString() });
      return {
        SKU: buyingOption.partId,
        name: buyingOption.originalPartNumber,
        category: this.setUndefined(cartEntry.partCategoryName),
        quantity: cartEntry.quantity,
        availability: (cartEntry.availableQuantity && cartEntry.minimumOrderQuantity) ? (cartEntry.availableQuantity >= cartEntry.minimumOrderQuantity ? 'In Stock' : 'No Stock') : undefined,
        shippingLocation: this.setUndefined(cartEntry.itemLocation),
        quantityMin: cartEntry.minimumOrderQuantity,
        quantityMax: cartEntry.availableQuantity,
        origin: this.setUndefined(cartEntry.countryOfOrigin),
        packagingStyle: this.setUndefined(cartEntry.packaging),
        mountingStyle: undefined,
        leadTime: buyingOption.manufacturerLeadTime,
        type: undefined,
        priceTotal: this.getItemLinePrice(addedProduct.quantity, buyingOption.priceBands),
        manufacturer: cartEntry.product.manufacturerName || cartEntry.manufacturer,
        hierarchy: this.setUndefined(cartEntry.partCategoryName),
        categoryPath: undefined
      }
    });
  }

  setUndefined(productData) {
    if (productData) {
      return productData;
    }
    else
    {
      return undefined;
    }
  }

  /**
   * Get the added item details for datalayer eCommerce
   *
   * @param {object} data
   * @param {AddedProduct[]} data.addedProducts - The product just added
   * @param {CartEntry[]} data.entries - Cart entries
   * @returns {Array}
   */
  getAddedItemDetails(data) {
    return _.map(data.addedProducts, addedProduct => {
      // Details of the buying option that was added
      let buyingOption = addedProduct.model;

      // The buying option details that the cart has returned. Will be item in array of other products
      let cartEntry = data.entries.find((cartItem)=> { return this.getPartNumber(cartItem) === buyingOption.partId.toString() });

      return this._getProductDatalayerAddToCart(addedProduct, buyingOption, cartEntry, data.event);
    });
  }

  /**
   * Get the eCommerce addToCart datalayer entry for a products
   *
   * @param {AddedProduct} addedProduct
   * @param {BuyingOption} buyingOption
   * @param {CartEntry} cartEntry
   * @returns {object}
   * @private
   */
  _getProductDatalayerAddToCart(addedProduct, buyingOption, cartEntry, event) {
    let product = {};
    cartEntry = cartEntry || {}; // make it an empty object if null
    product.name = buyingOption.originalPartNumber;
    product.id = buyingOption.partId;
    product.brand = cartEntry ? (cartEntry.manufacturer ? cartEntry.manufacturer.toUpperCase() : '') : '';
    product.category = this._scrapeCategory(buyingOption.partId);
    product.variant = cartEntry ? cartEntry.partDescription : '';
    product.price = this.getItemLinePrice(addedProduct.quantity, buyingOption.priceBands);
    product.quantity = addedProduct.quantity || '';

    if (!product.price) {
      product.price = addedProduct.model.price;
    }

    //Product added from Search
    if (addedProduct.model.position) {
      product.position = addedProduct.model.position;
    }
    let productSourcePartId = buyingOption.sourcePartId;
    //if Arrow Reel buying option, infer the sourcePartId
    if (buyingOption.isArrowReel) {
      productSourcePartId = productSourcePartId.replace(config.customReel.sourcePartIdSuffix,'');
    }

    // GA Custom Dimensions
    product.dimension12 = `${productSourcePartId || ''} / ${buyingOption.sourceCode || ''}`;
    product.dimension13 =  this._getProductImage(addedProduct); // Image name
    product.dimension14 = cartEntry.availableQuantity;          // Stock
    product.dimension17 = this._getProductDetailsImpressionValue('dimension17') || 1; // Buying options displayed
    product.dimension25 = true;                                 // Stock exists
    product.dimension26 = cartEntry.pedigree;                   // Product pedigree
    product.dimension27 = cartEntry.warranty;                   // Product warranty
    product.dimension29 = buyingOption.packagingType || 'Part'; // Packaging type
    product.dimension34 = cartEntry.shippingLeadTime;           // Delivery estimate in days
    product.dimension35 = addedProduct.model.shipsFromCountryName;  // Product shipping country of origin
    product.dimension36 = cartEntry.eccn;                       // ECCN code
    product.dimension37 = (cartEntry.rohsCompliant || false).toString().toLowerCase();   // ROHS compliance
    product.dimension38 = cartEntry.sellerId;                   // seller ID
    product.dimension39 = cartEntry.sellerType;                 // Seller type
    product.dimension41 = this._getProductDetailsImpressionValue('dimension41') || addedProduct.model.buyingOptionsCount || 1; // Regions displayed
    product.dimension42 = this._getProductDetailsImpressionValue('dimension42') || addedProduct.model.buyingOptionsCount;      // Regions available
    product.dimension43 = this._getProductDetailsImpressionValue('dimension43');      // Buying options available
    product.dimension44 = buyingOption.unitOfMeasure || 'Ea';   // Product unit of measure

    // product detail tracking
    if (buyingOption.npi === '') {
      product.dimension53 = this._getProductDetailsImpressionValue('dimension53');
    }
    // search npi tracking
    else {
      product.dimension53 = buyingOption.npi;
    }

    if (event === config.analytics.triggers.addToCartSearch || event === config.analytics.triggers.addToCart) {
      product.dimension58 = addedProduct.model.isDiscontinued;

      if (buyingOption.priceBands && buyingOption.priceBands.length > 0) {
        product.dimension78 = buyingOption.priceBands[0].hasTieredPricing;
      }
    }

    //promo group tracking
    let promoGroupAddToCartEvents =
      [
        config.analytics.triggers.addToCartSearch,
        config.analytics.triggers.addToCart
      ];
    if (promoGroupAddToCartEvents.indexOf(event) > -1) {
      if (addedProduct.model.promoGroup) {
        let validPromoGroup = this.validatePromoGroup(addedProduct.model.promoGroup);
        if (validPromoGroup) {
          product.dimension67 = addedProduct.model.promoGroup;
        }
      }
    }

    product.srcPartId = productSourcePartId || '';
    product.product_region = buyingOption.sourceCode || '';

    return product;
  }

  /**
   * If product is added from search, position will be populated.
   * If product is added from PDP, scrape the product image from the DOM.
   *  *
   * @returns {string|undefined}
   * @private
  */
  _getProductImage(addedProduct) {

    let imageUrl = addedProduct.model.position ? addedProduct.model.imageUrl : $('.Product-Summary').data('thumbnail');
    return imageUrl ? this._stripPathFromProductImage(imageUrl) : '';
  }

  _stripPathFromProductImage(imageUrl) {
    // Return just the filename without the path
    return imageUrl ? imageUrl.replace(/.*?\/([^\/]+)$/, '$1') : imageUrl;
  }
  /**
   * Get a value off of the productDetailsImpression datalayer event if it exists.
   *
   * @param {string} key
   * @returns {*}
   * @private
   */
  _getProductDetailsImpressionValue(key) {
    let productDetailsImpression = _.findWhere(window.dataLayer, { event: 'productDetailsImpression' });

    if (!productDetailsImpression) {
      return undefined;
    }

    return productDetailsImpression.ecommerce.detail.products[0][key];
  }

  /**
   * Get the line price of an item (with full decimal places, usually 4).
   *
   * @param {number} quantity - The quantity of the added item
   * @param {PriceBand[]} priceBands - The price bands of the item
   * @returns {number}
   */
  getItemLinePrice(quantity, priceBands) {
    let band = utilities.getPriceTierIndex(quantity, priceBands);
    if (band === -1) {
      return NaN;
    }
    return priceBands[band].price;
  }

  /**
   * Format the products to submit for removal event
   *
   * @param {object[]} products - The array of products to be formatted
   * @returns {object}
   */
  formatProductsForRemoval(products) {
    let removeObject = {
      remove: {
        products: []
      }
    };
    products.forEach((product) => {
      removeObject.remove.products.push({
        id: this.getPartNumber(product),
        name: product.mpn,
        brand: product.manufacturer,
        variant: product.partDescription,
        category: '', // per analytics team leave blank, not null
        price: product.unitPriceRaw ? product.unitPriceRaw : product.unitPrice,
        quantity: product.quantity,
        dimension12: `${product.catalogSourcePartId} / ${product.catalogSourceCode}`
      });
    });

    return removeObject;
  }

  adobeRemoveFromCart(cartData, cartAction) {
    let productsData, productList = [];
    let guid = '';
    if (cartAction === 'removeAllItems') {
      guid = (cartData && cartData.guid !== 'EMPTY_GUID') ? cartData.guid : undefined;
      productsData = cartData.entries;
    } else {
      const cartDetail = sessionStorage.getItem('cart') && JSON.parse(sessionStorage.getItem('cart'));
      productsData = cartData;
      guid = (cartDetail && cartDetail.guid !== 'EMPTY_GUID') ? cartDetail.guid : undefined;
    }
    productsData.forEach(product => {
      const availability = (product.availableQuantity && product.minimumOrderQuantity) ? (product.availableQuantity > product.minimumOrderQuantity ? 'In Stock' : 'No Stock') : undefined;
      productList.push({
        SKU: product.partNo,
        name: product.product.name,
        category: this.setUndefined(product.partCategoryName),
        quantity: product.quantity,
        availability: availability,
        shippingLocation: product.itemLocation,
        quantityMin: product.minimumOrderQuantity,
        quantityMax: product.availableQuantity,
        origin: product.countryOfOrigin,
        packagingStyle: this.setUndefined(product.packaging),
        mountingStyle: undefined,
        leadTime: undefined,
        type: undefined,
        priceTotal: product.totalPrice.displayValue,
        manufacturer: product.product.manufacturerName || product.manufacturer,
        hierarchy: this.setUndefined(product.partCategoryName),
        categoryPath: undefined
      });
    });

    window.adobeDataLayer.push({
      event: 'remove_from_cart',
      cartId: guid,
      products: productList
    });
  }

  getActionField() {
    let url = window.location.pathname;

    let pages = {
      '/search': config.analytics.actionFieldLists.searchPage,
      '/design-center/my-projects': config.analytics.actionFieldLists.designCenter
    };

    return _.find(pages, function(list, pageUrl) { return url.indexOf(pageUrl) > -1})
  }

  validatePromoGroup(promoGroup) {
    return config.analytics.promoGroups.indexOf(promoGroup) > -1;
  }
}

module.exports = new AnalyticsCart();



// WEBPACK FOOTER //
// ./assets/components/Analytics/AnalyticsCart.js